import React, { useState } from 'react';
import { Card, Typography, Button, Space, message, Input, Alert, Tag, Descriptions, Modal } from 'antd';
import { CopyOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompany } from '@/hooks/useCompanies';
import apiClient from '@/api/client';

const { Title, Text } = Typography;

const TokenManager: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: company, isLoading, refetch } = useCompany(id || '');
  const [showToken, setShowToken] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleCopyToken = () => {
    if (company?.registration_token) {
      navigator.clipboard.writeText(company.registration_token);
      message.success('Token copiado para a área de transferência!');
    }
  };

  const handleCopyCommand = (os: 'windows' | 'linux') => {
    if (!company?.registration_token) return;
    
    const serverUrl = window.location.origin;
    let command = '';
    
    if (os === 'windows') {
      command = `powershell -ExecutionPolicy Bypass -File install-windows.ps1 -ServerUrl "${serverUrl}" -Token "${company.registration_token}"`;
    } else {
      command = `sudo bash install-linux.sh --server "${serverUrl}" --token "${company.registration_token}"`;
    }
    
    navigator.clipboard.writeText(command);
    message.success('Comando copiado!');
  };

  const handleRegenerateToken = () => {
    Modal.confirm({
      title: 'Regenerar Token',
      icon: <ExclamationCircleOutlined />,
      content: 'ATENÇÃO: Ao regenerar o token, todos os scripts de instalação antigos deixarão de funcionar. Dispositivos já instalados continuarão funcionando normalmente. Deseja continuar?',
      okText: 'Sim, regenerar',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        setRegenerating(true);
        try {
          await apiClient.post(`/companies/${id}/regenerate_token/`);
          message.success('Token regenerado com sucesso!');
          refetch();
        } catch (error) {
          message.error('Erro ao regenerar token.');
        } finally {
          setRegenerating(false);
        }
      },
    });
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  if (!company) {
    return <div>Empresa não encontrada</div>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => navigate(`/companies/${id}`)}>Voltar</Button>
        <Title level={4} style={{ margin: 0 }}>Gerenciar Token - {company.trade_name || company.name}</Title>
      </Space>

      {/* Token atual */}
      <Card title="Token de Registro" style={{ marginBottom: 16 }}>
        <Alert
          message="Importante"
          description="Este token é usado para registrar novos dispositivos. Mantenha-o em segurança e compartilhe apenas com técnicos autorizados."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Token">
            <Space>
              <Input
                value={company.registration_token}
                type={showToken ? 'text' : 'password'}
                readOnly
                style={{ width: 400, fontFamily: 'monospace' }}
              />
              <Button
                icon={showToken ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowToken(!showToken)}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyToken}
              >
                Copiar
              </Button>
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Status">
            <Tag color={company.require_token_for_registration ? 'green' : 'orange'}>
              {company.require_token_for_registration ? 'Obrigatório' : 'Opcional'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 16 }}>
          <Button
            danger
            icon={<ReloadOutlined />}
            loading={regenerating}
            onClick={handleRegenerateToken}
          >
            Regenerar Token
          </Button>
        </div>
      </Card>

      {/* Comandos de instalação */}
      <Card title="Comandos de Instalação" style={{ marginBottom: 16 }}>
        <Alert
          message="Use estes comandos para instalar o RustDesk com registro automático"
          description="O token já está incluído no comando. Basta copiar e executar no computador do cliente."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <Text strong>Windows (PowerShell):</Text>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Input.TextArea
              value={`powershell -ExecutionPolicy Bypass -File install-windows.ps1 -ServerUrl "${window.location.origin}" -Token "${company.registration_token}"`}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => handleCopyCommand('windows')}
            >
              Copiar
            </Button>
          </div>
        </div>

        <div>
          <Text strong>Linux (Bash):</Text>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Input.TextArea
              value={`sudo bash install-linux.sh --server "${window.location.origin}" --token "${company.registration_token}"`}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => handleCopyCommand('linux')}
            >
              Copiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Instruções */}
      <Card title="Instruções para Técnicos">
        <ol>
          <li>Envie o script de instalação correspondente ao sistema operacional do cliente</li>
          <li>O cliente deve executar o script como administrador</li>
          <li>O script instalará o RustDesk e registrará automaticamente o dispositivo</li>
          <li>O dispositivo aparecerá no painel em "Dispositivos"</li>
          <li>O heartbeat mantém o status atualizado automaticamente</li>
        </ol>
      </Card>
    </div>
  );
};

export default TokenManager;
